import { type PageContext } from '@/hooks/use-folio-stream';
import { useAuth } from '@/hooks/use-auth';

const GUIDE_BOOTSTRAP_STORAGE_KEY_PREFIX = 'ca-folio-guide-bootstrap-attempted:v2';
const GUIDE_PULSE_STORAGE_KEY_PREFIX = 'ca-folio-guide-pulse:v2';

/** Custom event fired when the pulse state changes in localStorage. */
export const GUIDE_PULSE_CHANGE_EVENT = 'ca-folio-guide-pulse-change';

// 'has_history' records that the user already had Sean conversations, so later visits
// skip both the history lookup and the guide pulse.
export type GuideBootstrapStatus = 'not_started' | 'bootstrapping' | 'active' | 'deferred' | 'has_history';

export interface GuideBootstrapState {
  readonly status: GuideBootstrapStatus;
  readonly conversationId: string | null;
}

const DEFAULT_GUIDE_BOOTSTRAP_STATE: GuideBootstrapState = {
  status: 'not_started',
  conversationId: null,
};

export function useGuideStorageUserId(): string | null {
  return useAuth().user?.user_id ?? null;
}

function readStorageValue(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorageValue(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    return;
  }
}

function removeStorageValue(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    return;
  }
}

function parseJsonValue(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function isGuideBootstrapStatus(value: unknown): value is GuideBootstrapStatus {
  return (
    value === 'not_started' ||
    value === 'bootstrapping' ||
    value === 'active' ||
    value === 'deferred' ||
    value === 'has_history'
  );
}

export function buildGuideBootstrapStorageKey(pageContext: PageContext, userId: string | null): string {
  const scope =
    pageContext.entities.project_id ??
    pageContext.entities.tenant_id ??
    pageContext.entities.tenant_slug ??
    'global';
  return `${GUIDE_BOOTSTRAP_STORAGE_KEY_PREFIX}:${scope}:${userId ?? 'anonymous'}`;
}

export function readGuideBootstrapState(pageContext: PageContext, userId: string | null): GuideBootstrapState {
  const storedValue = readStorageValue(buildGuideBootstrapStorageKey(pageContext, userId));
  if (storedValue === null) {
    return DEFAULT_GUIDE_BOOTSTRAP_STATE;
  }

  // Legacy bootstrap sentinel from the pulse-only implementation.
  if (storedValue === '1') {
    return {
      status: 'deferred',
      conversationId: null,
    };
  }

  const parsed = parseJsonValue(storedValue);
  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    !('status' in parsed) ||
    !isGuideBootstrapStatus(parsed.status)
  ) {
    return DEFAULT_GUIDE_BOOTSTRAP_STATE;
  }

  const conversationId =
    'conversationId' in parsed && (typeof parsed.conversationId === 'string' || parsed.conversationId === null)
      ? parsed.conversationId
      : null;

  return {
    status: parsed.status,
    conversationId,
  };
}

function writeGuideBootstrapState(pageContext: PageContext, userId: string | null, state: GuideBootstrapState) {
  writeStorageValue(buildGuideBootstrapStorageKey(pageContext, userId), JSON.stringify(state));
}

export function hasGuideBootstrapAttempted(pageContext: PageContext, userId: string | null): boolean {
  return readGuideBootstrapState(pageContext, userId).status !== 'not_started';
}

export function markGuideBootstrapAttempted(pageContext: PageContext, userId: string | null) {
  writeGuideBootstrapState(pageContext, userId, {
    status: 'bootstrapping',
    conversationId: null,
  });
}

export function markGuideBootstrapActive(pageContext: PageContext, userId: string | null, conversationId: string | null) {
  writeGuideBootstrapState(pageContext, userId, {
    status: 'active',
    conversationId,
  });
}

export function markGuideDeferred(pageContext: PageContext, userId: string | null, conversationId: string | null) {
  writeGuideBootstrapState(pageContext, userId, {
    status: 'deferred',
    conversationId,
  });
}

export function markGuideHistoryFound(pageContext: PageContext, userId: string | null) {
  writeGuideBootstrapState(pageContext, userId, {
    status: 'has_history',
    conversationId: null,
  });
}

function buildGuidePulseStorageKey(pageContext: PageContext, userId: string | null): string {
  const scope =
    pageContext.entities.tenant_id ?? pageContext.entities.tenant_slug ?? 'global';
  return `${GUIDE_PULSE_STORAGE_KEY_PREFIX}:${scope}:${userId ?? 'anonymous'}`;
}

export function hasGuidePulseNeeded(pageContext: PageContext, userId: string | null): boolean {
  return readStorageValue(buildGuidePulseStorageKey(pageContext, userId)) !== null;
}

export function markGuidePulseNeeded(pageContext: PageContext, userId: string | null) {
  writeStorageValue(buildGuidePulseStorageKey(pageContext, userId), '1');
  window.dispatchEvent(new CustomEvent(GUIDE_PULSE_CHANGE_EVENT));
}

export function clearGuidePulse(pageContext: PageContext, userId: string | null) {
  removeStorageValue(buildGuidePulseStorageKey(pageContext, userId));
  window.dispatchEvent(new CustomEvent(GUIDE_PULSE_CHANGE_EVENT));
}
